import * as React from "react";
import PropTypes from "prop-types";
import posthog from "posthog-js";
import { identityTransition } from "../domain/analyticsIdentity";
import { useUser } from "../globalState";

const PostHogContext = React.createContext(null);

const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
const host = import.meta.env.VITE_POSTHOG_HOST;

const isEnabled = Boolean(apiKey && host);

const initPostHog = () => {
  posthog.init(apiKey, {
    api_host: host,
    defaults: "2026-01-30",
    capture_pageview: false,
    capture_performance: false,
    autocapture: false,
    persistence: "localStorage+cookie",
  });

  posthog.register({
    environment: import.meta.env.VITE_ENV || "development",
    app_version: import.meta.env.VITE_APP_VERSION,
    platform: "web",
  });
};

// Before first render: child effects (TrackingProvider's pageview) run before this provider's.
if (isEnabled) initPostHog();

export function usePostHog() {
  return React.useContext(PostHogContext);
}

export default function PostHogProvider({ children }) {
  const user = useUser();

  React.useEffect(() => {
    if (!isEnabled) return;

    const appUserId =
      user?.isLoggedIn && user?.userId ? String(user.userId) : null;
    const effects = {
      reset: () => posthog.reset(),
      identify: () =>
        posthog.identify(
          appUserId,
          { email: user.email, user_type: user.userType },
          { initial_referrer: document.referrer },
        ),
    };

    identityTransition({
      // Set by identify(), cleared by reset(); get_distinct_id() would return the anonymous id.
      posthogUserId: posthog.get_property("$user_id") ?? null,
      appUserId,
    }).forEach((action) => effects[action]());
  }, [user?.isLoggedIn, user?.userId, user?.email, user?.userType]);

  const client = React.useMemo(() => (isEnabled ? posthog : null), []);

  return (
    <PostHogContext.Provider value={client}>{children}</PostHogContext.Provider>
  );
}

PostHogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
